import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Practitioner } from "../../model/v2/practitioner/practitioner";
import { SearchPatientReq } from "../../model/v2/practice/searchPatientList";
import { HospitalAffiliationList } from "../../model/v2/hospitalAffiliation";
import { GlobalHealthSystem } from "../../model/v2/healthSystem/global-health-system";
import { HealthOrganizationSetupDomain } from "../../model/v2/practice/HealthOrganizationSetupDomain";
import { BaseService } from "../shared/base.service";
import { HealthOrganizationCheckoutSummary } from "../../model/v2/practice/RoleGroupedUser";
import { AppyUser } from "../../model/v2/AppyUser";
import { PractitionerDayOff } from "../../model/v2/practitioner/practitioner-day-off";
import { PaymentTransactionDTO } from "../../model/v2/PaymentTransactionDTO";

import { PagedResult } from "../../../core/models/PageRequestInterface";
import { ResultBase } from "../../../core/models/result-base";
import { HealthOrganizationData } from "../../model/v2/practice/HealthOrganizationData";
import { HealthOrganizationStatus } from "../../models/healthOrganizationStatus";
import { Symptom } from "../../model/v2/practitioner/symptom";
import { PractitionerSymptomsUpdate } from "../../model/v2/practitioner/practitioner-symptoms-update";
import { PractitionerReviewSummary } from "../../model/v2/practice/practitioner-review-summary";
import { PractitionerReviewDetail } from "../../model/v2/practice/practitioner-review-detail";
import {
  PracticeServiceOffer,
  ServiceToOffer,
} from "../../models/healthSystem/serviceToOffer";
import { HealthOrganizationUser } from "src/app/admin/models/healthOrganizationUser";
import { AnyCnameRecord } from "dns";
import { PractitionerDocument } from "../../models/practitioner/practitioner-document";
import { PractitionerEvent } from "../../model/v2/practitioner/practitioner-event";
import { PractitionerTreatmentsUpdate } from "../../model/v2/practitioner/practitionerTreatmentsUpdate";
import { Treatment } from "../../model/v2/practitioner/treatment";
import { Healthsystemwithaddress } from "../../model/v2/healthSystem/healthsystemwithaddress.model";
import { Treatmentlocation } from "../../model/v2/practitioner/treatmentlocation";
import { PractitionerTreatmentLocationrequest } from "../../model/v2/practitioner/practitioner-treatment-locationrequest";
import { Treatmentlocationrequest } from "../../model/v2/practitioner/treatmentlocationrequest";
import { PractitionerCondition } from "../../model/v2/practitioner/practitioner-condition";
import { Condition } from "../../model/v2/practitioner/condition";
import { PractitionerConditionsUpdate } from "../../model/v2/practitioner/practitioner-conditions-update";
import { PractitionerServices } from "../../model/v2/practitioner/practitioner-services";

@Injectable({
  providedIn: "root",
})
export class PracticeService {
  toggleSidebar: boolean = false;
  private currentUser: AppyUser;

  constructor(private baseService: BaseService) {}

  shouldRedirectToSetupFlow() {
    if (this.currentUser)
      if (
        this.currentUser.role == "Administrator" ||
        this.currentUser.isGlobalAdministrator
      )
        return (
          !this.currentUser.isProfileSetUpCompleted &&
          !this.currentUser.isHOAccessActive
        );

    return false;
  }

  isCurrentUserActive() {
    if (this.currentUser) {
      return this.currentUser.isActive;
    }
    return true;
  }

  isCurrentHOAccessActive() {
    if (this.currentUser) {
      return this.currentUser.isHOAccessActive;
    }
    return true;
  }

  checkForAdmin(): boolean {
    return this.isAdmin();
  }
  checkForAHI(): boolean {
    return this.isAHIAdmin();
  }

  checkProfileActive(): boolean {
    return this.isProfileActive();
  }

  isAdmin(): boolean {
    if (this.currentUser) {
      if (
        this.currentUser.role === "Administrator" ||
        this.currentUser.isGlobalAdministrator ||
        this.currentUser.role === "Practice Manager"
      ) {
        return true;
      }
    }

    return false;
  }

  isAHIAdmin(): boolean {
    return this.currentUser && this.currentUser.role === "AHIAdministrator";
  }

  isProfileActive(): boolean {
    return this.currentUser && this.currentUser.isProfileSetUpCompleted;
  }

  currentUserGlobalAdministrator() {
    if (this.currentUser) return this.currentUser.isGlobalAdministrator;

    return false;
  }

  currentUserIsPactitioner() {
    if (this.currentUser) return this.currentUser.hasPractitioner;

    return false;
  }

  currentUserIsStaff() {
    if (this.currentUser) return this.currentUser.userRoleCategory == "Staff";

    return false;
  }

  getCurrentUser(): AppyUser {
    return this.currentUser;
  }

  setCurrentUser(user: AppyUser) {
    this.currentUser = user;
  }

  searchGlobalHealthSystem(npi: string): Observable<GlobalHealthSystem> {
    return this.baseService.get<GlobalHealthSystem>(
      `GlobalData/GetGlobalHealthSystemDetailsByNPI?npi=${npi}`
    );
  }

  searchLocalHealthSystem(npi: string): Observable<GlobalHealthSystem> {
    return this.baseService.get<GlobalHealthSystem>(
      `HealthSystem/GetHealthSystemByNPI?healthSystemNPI=${npi}`
    );
  }

  searchPhysicians(npi: string): Observable<Practitioner> {
    return this.baseService.get<Practitioner>(
      `GlobalData/GetGlobalPractitionerDetailsByNPI?npi=${npi}`
    );
  }

  addHealthSystemsToHO(hsReqData: any): Observable<any> {
    let url = `HealthSystem/AddHealthSystemsToHO`;
    if (hsReqData.healthSystems[0].practiceType == 2) {
      url = `LegalPractice/AddLegalPracticeToHO`;
    }
    return this.baseService.post<any>(url, hsReqData);
  }
  /**
   * @deprecated use AddPractitioners that does not require that the userId be sent, since it will retrieve it from the authenticated user
   */
  addPractitionersToHS(practReqData: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/AddPractitionersToHS`,
      practReqData
    );
  }

  addPractitioners(practReqData: any): Observable<any> {
    if (practReqData.practitioners[0].role == "Attorney") {
      return this.baseService.post<any>(
        `LegalPractitioner/AddLegalPractitioners`,
        practReqData
      );
    } else {
      return this.baseService.post<any>(
        `Practitioner/AddPractitioners`,
        practReqData
      );
    }
  }

  updatePractitioner(practitioner: Practitioner): Observable<any> {
    if (practitioner.role == "Attorney") {
      return this.baseService.post<any>(
        `LegalPractitioner/UpdateLegalPractitioner`,
        practitioner
      );
    } else {
      return this.baseService.post<any>(
        `Practitioner/UpdatePractitioner`,
        practitioner
      );
    }
  }

  deletePractitioner(practitionerId: number): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerById?practitionerId=${practitionerId}`
    );
  }
  deletePractitionerSymptom(practitionerSymptomId: number): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerSymptom?practitionerSymptomId=${practitionerSymptomId}`
    );
  }
  deletePractitionerTreatment(
    practitionerTreatmentId: number
  ): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerTreatment?practitionerTreatmentId=${practitionerTreatmentId}`
    );
  }
  deletePractitionerCondition(
    practitionerConditionId: number
  ): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerCondition?practitionerConditionId=${practitionerConditionId}`
    );
  }
  getHealthOrganizationDetailsByLoggedUser(): Observable<HealthOrganizationSetupDomain> {
    return this.baseService.get<HealthOrganizationSetupDomain>(
      `HealthOrganization/GetHealthOrganizationDetailsByLoggedUser`
    );
  }

  getPractitionerPatientsList(req: SearchPatientReq): Observable<any> {
    return this.baseService.post(
      `Practitioner/GetPractitionerPatientsList`,
      req
    );
  }

  GetPatientsListByOrgId(req: SearchPatientReq): Observable<any> {
    return this.baseService.post(
      `Patients/GetPatientsListByOrgId`,
      req
    );
  }

  getAllHealthSystemAndPractitionersByUserID(userId: number): Observable<any> {
    return this.baseService.get<any>(
      `User/GetAllHealthSystemAndPractitionersByUserID?userId=${userId}`
    );
  }

  getAppointmentsBySearchCriteria(criteria: any): Observable<any> {
    return this.baseService.post<any>(
      `AppointmentContoller/GetAppointmentsBySearchCriteria`,
      criteria
    );
  }

  getPractitionerDetails(id: number): Observable<Practitioner> {
    return this.baseService.get<Practitioner>(
      `Practitioner/GetPractitionerDetailsById?practitionerId=${id}`
    );
  }
  getPractitionerConditionsByPractitionerId(id: number): Observable<PractitionerCondition[]> {
    return this.baseService.get<PractitionerCondition[]>(
      `Practitioner/GetPractitionerConditionsByPractitionerId?practitionerId=${id}`
    );
  }
  getPractitionerServicesByPractitionerId(id: number): Observable<PractitionerServices> {
    return this.baseService.get<PractitionerServices>(
      `Practitioner/GetPractitionerServicesByPractitionerId?practitionerId=${id}`
    );
  }
  updateCondition(condition: Condition): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/UpdateCondition`,
      condition
    );
  }

  getPractitionerDetailsByNpi(npi: string): Observable<Practitioner> {
    return this.baseService.get<Practitioner>(
      `Practitioner/GetGlobalPractitionerByNPI?practitionerNpi=${npi}`
    );
  }

  gethealthSystemDetails(
    id: number,
    practiceType: number = 0
  ): Observable<GlobalHealthSystem> {
    let url = `HealthSystem/GetHealthSystemById?healthSystemId=${id}`;
    if (practiceType == 2) {
      url = `LegalPractice/GetLegalPracticeById?legalPracticeById=${id}`;
    }
    return this.baseService.get<GlobalHealthSystem>(url);
  }

  gethealthSystemServices(
    id: number
  ): Observable<ResultBase<ServiceToOffer[]>> {
    return this.baseService.get<ResultBase<ServiceToOffer[]>>(
      `HealthSystem/${id}/GetServices`
    );
  }

  updatePhysicianAboutMe(
    aboutMe: string,
    practitionerId: number
  ): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/UpdatePractitionerAboutMe`,
      { practitionerId: practitionerId, aboutMe: aboutMe }
    );
  }
  updatePractitionerInfo(
    physicianbio: any,
    physicianName: any,
    physicianAboutMe: any,
    hospitalAffiliationRequest:any
  ): Observable<any> {
    return this.baseService.post<any>(`Practitioner/UpdatePractitionerInfo`, {
      practitionerBio: physicianbio,
      practitionerAboutMe: physicianAboutMe,
      updatePractitionerInsurancePlans: {},
      practitionerName: physicianName,
      hospitalAffiliationRequest:hospitalAffiliationRequest
    });
  }
  updatePhysicianBio(physicianBio: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/UpdatePractitionerBio`,
      physicianBio
    );
  }

  updatePractitionerWorkingHours(
    physicianWH: any
  ): Observable<ResultBase<boolean>> {
    return this.baseService.post<ResultBase<boolean>>(
      `Practitioner/UpdatePractitionerWorkingHours`,
      physicianWH
    );
  }

  updatePractitionerLocation(
    practitionerLocation: any
  ): Observable<ResultBase<boolean>> {
    return this.baseService.post<ResultBase<boolean>>(
      `Practitioner/UpdatePractitionerLocation`,
      practitionerLocation
    );
  }

  deletePractitionerLocation(
    practitionerLocation: any
  ): Observable<ResultBase<boolean>> {
    return this.baseService.post<ResultBase<boolean>>(
      `Practitioner/DeletePractitionerLocation`,
      practitionerLocation
    );
  }

  updatePhysicianName(physicianName: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/UpdatePractitionerName`,
      physicianName
    );
  }
  addnewTreatment(name: any): Observable<any> {
    let trt = new Treatment();
    trt.name = name;
    return this.baseService.post<any>(`Practitioner/AddNewTreatment`, trt);
  }
  addnewCondition(condition: any): Observable<any> {
    let trt = new Condition();
    trt = condition;
    return this.baseService.post<any>(`Practitioner/AddNewCondition`, trt);
  }
  addnewSymptom(name: any): Observable<any> {
    let symp = new Symptom();
    symp.name = name;
    return this.baseService.post<any>(`Practitioner/AddNewSymptom`, symp);
  }

  addnewService(name: any): Observable<any> {
    let serv = new PracticeServiceOffer();
    serv.name = name;
    return this.baseService.post<any>(`Practitioner/AddNewService`, serv);
  }
  getHealthSystemsByName(name: string): Observable<HospitalAffiliationList> {
    
    return this.baseService.get<HospitalAffiliationList>(
      `Practitioner/GetHealthSystemsByName?searchText=${name}`
    );
  }

  getHealthSystemByNPI(npi: string): Observable<GlobalHealthSystem> {
    return this.baseService.get<GlobalHealthSystem>(
      `Practitioner/GetHealthSystemByNPI?npi=${npi}`
    );
  }
  getHealthSystemWithAddressByNPI(npi: string): Observable<Healthsystemwithaddress> {
    return this.baseService.get<Healthsystemwithaddress>(
      `Practitioner/GetHealthSystemWithAddressByNPI?npi=${npi}`
    );
  }
  updatePhysicianAffiliation(physicianAffiliation: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/AddPractitionerHospitalAffiliation`,
      physicianAffiliation
    );
  }

  deletePhysicianAffiliation(physicianAffiliation: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/DeletePractitionerHospitalAffiliation`,
      physicianAffiliation
    );
  }

  deleteHealthSystemById(healthSystemId: number): Observable<any> {
    return this.baseService.delete<any>(
      `HealthSystem/DeleteHealthSystemById?healthSystemId=${healthSystemId}`
    );
  }
  deleteHealthSystemServiceById(serviceId: number, hsid:number): Observable<any> {
    return this.baseService.delete<any>(
      `HealthSystem/DeleteHealthSystemServiceById?serviceId=${serviceId}&hsid=${hsid}`
    );
  }

  uploadtProfilePicture(payload: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Practitioner/UploadPractitionerProfilePicture`,
      payload
    );
  }
  uploadtPractitionerServicePicture(payload: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Practitioner/UploadPractitionerServicesPicture`,
      payload
    );
  }

  uploadPractitionerPatientDocument(
    practitionerId: number,
    file: any
  ): Observable<any> {
    const docForm: FormData = new FormData();
    docForm.append("File", file, file.name);
    docForm.append("PractitionerId", practitionerId.toString());

    return this.baseService.upload<any>(
      `Practitioner/UploadPractitionerPatientDocument`,
      docForm
    );
  }

  deletePractitionerPatientDocument(documentId: any): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerPatientDocument?documentId=${documentId}`
    );
  }

  getPractitionerPatientDocument(practId: number): Observable<any> {
    return this.baseService.get<PractitionerDocument>(
      `Practitioner/GetPractitionerPatientDocument?practitionerId=${practId}`
    );
  }

  uploadPractitionerReferralAttachment(
    practitionerReferralId: number,
    file: any
  ): Observable<any> {
    const docForm: FormData = new FormData();
    docForm.append("File", file, file.name);
    docForm.append("PractitionerReferralId", practitionerReferralId.toString());

    return this.baseService.upload<any>(
      `Referral/UploadPractitionerReferralAttachment`,
      docForm
    );
  }

  deletePractitionerReferralAttachment(documentId: number): Observable<void> {
    return this.baseService.delete<void>(
      `Referral/DeletePractitionerReferralAttachment?documentId=${documentId}`
    );
  }

  updateInsurances(data) {
    return this.baseService.post<any>(
      `Practitioner/UpdatePractitionerInsurancePlans`,
      data
    );
  }

  updateHealthSystem(data) {
    let url = `HealthSystem/UpdateHealthSystem`;
    if (data.practiceType == 2) {
      url = `LegalPractice/UpdateLegalPractice`;
    }
    return this.baseService.post<any>(url, data);
  }

  // updateHealthSystemByNPI(data) {
  //   return this.baseService.post<any>(`HealthSystem/UpdateHealthSystemByNPI`, data);
  // }

  updateHealthSysInsurances(data) {
    return this.baseService.post<any>(
      `HealthSystem/UpdateHealthSystemInsurancePlans`,
      data
    );
  }

  updateHealthSysServices(data) {
    return this.baseService.post<any>(
      `HealthSystem/UpdateHealthSystemServicesToOffer`,
      data
    );
  }

  updateHealthSystemAboutUs(
    aboutUs: string,
    healthSystemId: number
  ): Observable<any> {
    return this.baseService.post<any>(
      `HealthSystem/UpdateHealthSystemAboutUs`,
      { healthSystemId: healthSystemId, aboutUs: aboutUs }
    );
  }

  updateHealthSystemInfo(payload: any): Observable<any> {
    return this.baseService.post(
      `HealthSystem/UpdateHealthSystemInfo`,
      payload
    );
  }

  uploadHealthSystemProfilePicture(payload: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `HealthSystem/UploadHealthSystemProfilePicture`,
      payload
    );
  }

  updateHealthSystemContactInfo(contactInfo: any): Observable<any> {
    return this.baseService.post<any>(
      `HealthSystem/UpdateHealthSystemContactInfo`,
      contactInfo
    );
  }

  getPractitionerFavorites(practId: number, type: string): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetPractitionerFavorites?practitionerId=${practId}&type=${type}`
    );
  }

  searchPractitionerFavorites(req: SearchPatientReq): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/SearchPractitionerFavorites`,
      req
    );
  }

  getPractitionersByName(searchText: string, zipCode: string): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetPractitionersByName?searchText=${searchText}&zipCode=${zipCode}`
    );
  }

  getNearestPractitionersByName(searchText: string): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetNearestPractitionersByName?searchText=${searchText}`
    );
  }

  getPractitionersBySpecialtyName(
    specialty: string,
    zipCode: string
  ): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetPractitionersBySpecialtyName?specialty=${specialty}&zipCode=${zipCode}`
    );
  }

  addPractitionerFavorites(practFav: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/AddPractitionerFavorite`,
      practFav
    );
  }

  validateICDCode(icdCode: string): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/ValidateICDCode?ICDCode=${icdCode}`
    );
  }

  addPractitionerReferral(referral: any): Observable<any> {
    return this.baseService.post<any>(
      `Practitioner/AddPractitionerReferral`,
      referral
    );
  }

  getHealthOrganizationCheckoutInformation(): Observable<HealthOrganizationCheckoutSummary> {
    return this.baseService.get<HealthOrganizationCheckoutSummary>(
      `HealthOrganization/GetHealthOrganizationCheckoutInformation`
    );
  }

  ChargeUserOrganizationForTheOneTimeFeeAndUpdateSubscription(): Observable<any> {
    return this.baseService.post<any>(
      `HealthOrganization/ChargeUserOrganizationForTheOneTimeFeeAndUpdateSubscription`,
      null
    );
  }

  deletePractitionerFavorites(practFavId: number): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerFavorite?favoriteId=${practFavId}`
    );
  }

  getNextPractitionerDayOff(
    practitionerId: number,
    healthSystemId: number
  ): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetNextPractitionerDayOff?PractitionerId=${practitionerId}&HealthSystemId=${healthSystemId}`
    );
  }

  getPractitionerDayOff(
    practitionerId: number,
    healthSystemId: number
  ): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetPractitionerDayOff?PractitionerId=${practitionerId}&HealthSystemId=${healthSystemId}`
    );
  }

  addPractitionerDayOff(
    practitionerDayOff: PractitionerDayOff
  ): Observable<any> {
    return this.baseService.post<any>(
      "Practitioner/AddPractitionerDayOff",
      practitionerDayOff
    );
  }

  updatePractitionerDayOff(
    practitionerDayOff: PractitionerDayOff
  ): Observable<any> {
    return this.baseService.put<any>(
      "Practitioner/UpdatePractitionerDayOff",
      practitionerDayOff
    );
  }

  deletePractitionerDayOff(dayOffId: number): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerDayOff?dayOffId=${dayOffId}`
    );
  }

  getPractitionerEvents(
    practitionerId: number,
    pageIndex: number,
    itemsCount: number
  ): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/GetPractitionerEvents?practitionerId=${practitionerId}&pageIndex=${pageIndex}&itemsCount=${itemsCount}`
    );
  }

  addPractitionerEvent(event: PractitionerEvent): Observable<any> {
    console.log(event.endDate, "====#Event1====", event.startDate);
    return this.baseService.post<any>(
      "Practitioner/AddPractitionerEvent",
      event
    );
  }

  updatePractitionerEvent(event: PractitionerEvent): Observable<any> {
    return this.baseService.post<any>(
      "Practitioner/UpdatePractitionerEvent",
      event
    );
  }

  patientCanBeReffered(
    practitionerId: number,
    patientId: number
  ): Observable<any> {
    return this.baseService.get<any>(
      `Practitioner/PatientCanBeReffered?PractitionerId=${practitionerId}&PatientId=${patientId}`
    );
  }

  getHealthOrganizationPayments(
    pageIndex: number,
    pageSize: number
  ): Observable<PagedResult<PaymentTransactionDTO>> {
    return this.baseService.get<PagedResult<PaymentTransactionDTO>>(
      `HealthOrganization/PaymentTransactions?pageIndex=${pageIndex}&pageSize=${pageSize}`
    );
  }

  GetAllHealthOrganizationsCheckoutStatus(
    pageIndex: number,
    pageSize: number,
    activeStatus: number,
    isProfileSetUpCompleted=true
  ): Observable<PagedResult<HealthOrganizationStatus>> {
    return this.baseService.get<PagedResult<HealthOrganizationStatus>>(
      `HealthOrganization/GetAllHealthOrganizationsCheckoutStatus?pageIndex=${pageIndex}&pageSize=${pageSize}&activeStatus=${activeStatus}&isProfileSetUpCompleted=${isProfileSetUpCompleted}`
    );
  }

  getAllOrganizationRegistrations(pageIndex: number, pageSize: number,): Observable<PagedResult<any>> {
    return this.baseService.get<any>(
      `HealthOrganization/GetOrganizationRegistrations?pageIndex=${pageIndex}&pageSize=${pageSize}`
    );
  }

  isReadyToCheckout(): Observable<ResultBase<boolean>> {
    return this.baseService.get<ResultBase<boolean>>(
      `HealthOrganization/IsReadyToCheckout`
    );
  }

  getHealthOrganizationData(): Observable<ResultBase<HealthOrganizationData>> {
    return this.baseService.get<ResultBase<HealthOrganizationData>>(
      `HealthOrganization`
    );
  }

  updateHealthOrganizationData(
    hoDto: HealthOrganizationData
  ): Observable<ResultBase<HealthOrganizationData>> {
    return this.baseService.put<ResultBase<HealthOrganizationData>>(
      `HealthOrganization`,
      hoDto
    );
  }

  getSymptomsByName(name: string): Observable<Symptom[]> {
    return this.baseService.get<Symptom[]>(
      `Practitioner/GetSymptomsByName?name=${name}`
    );
  }
  getTreatmentsByName(name: string): Observable<Treatment[]> {
    return this.baseService.get<Treatment[]>(
      `Practitioner/GetTreatmentsByName?name=${name}`
    );
  }
  getPractitionerSymptoms(practitionerId: number): Observable<Symptom[]> {
    return this.baseService.get<Symptom[]>(
      `Practitioner/GetSymptoms/${practitionerId}`
    );
  }
  getPractitionerTreatments(practitionerId: number): Observable<Treatment[]> {
    return this.baseService.get<Treatment[]>(
      `Practitioner/GetTreatments/${practitionerId}`
    );
  }
  getConditions(conditionType: string): Observable<Condition[]> {
    return this.baseService.get<Condition[]>(
      `Practitioner/GetConditions?conditionType=${conditionType}`
    );
  }
  getPractitionerTreatmentsWithLocation(practitionerId: number): Observable<PractitionerTreatmentLocationrequest> {
    return this.baseService.get<PractitionerTreatmentLocationrequest>(
      `Practitioner/GetTreatmentsWithLocation/${practitionerId}`
    );
  }
  addOrUpdateSymptoms(
    addSymptomsRequest: PractitionerSymptomsUpdate
  ): Observable<Symptom[]> {
    return this.baseService.post<Symptom[]>(
      `Practitioner/AddOrUpdateSymptoms`,
      addSymptomsRequest
    );
  }
  addOrUpdateTreatments(
    addTreatmentsRequest: PractitionerTreatmentsUpdate
  ): Observable<Treatment[]> {
    return this.baseService.post<Treatment[]>(
      `Practitioner/AddOrUpdateTreatments`,
      addTreatmentsRequest
    );
  }
  addOrUpdateConditions(
    addConditionsRequest: PractitionerConditionsUpdate
  ): Observable<Condition[]> {
    return this.baseService.post<Condition[]>(
      `Practitioner/AddOrUpdateConditions`,
      addConditionsRequest
    );
  }
  addOrUpdateServices(
    addServicesRequest: PractitionerServices
  ): Observable<any> {
    return this.baseService.post<PractitionerServices>(
      `Practitioner/AddOrUpdatePractitionerServices`,
      addServicesRequest
    );
  }

  addOrUpdateTreatmentsLocation(
    addTreatmentsLocationRequest: PractitionerTreatmentLocationrequest
  ): Observable<PractitionerTreatmentLocationrequest[]> {
    return this.baseService.post<PractitionerTreatmentLocationrequest[]>(
      `Practitioner/AddOrUpdateTreatmentsLocation`,
      addTreatmentsLocationRequest
    );
  }
  activateTrial(): Observable<ResultBase<boolean>> {
    return this.baseService.post<ResultBase<boolean>>(
      `HealthOrganization/ActivateTrial`,
      null
    );
  }

  updateEndOfTrialDate(
    hoID: number,
    newEndOfTrialDate: Date
  ): Observable<ResultBase<Date>> {
    return this.baseService.put<ResultBase<Date>>(
      `HealthOrganization/${hoID}/UpdateEndOfTrial`,
      newEndOfTrialDate
    );
  }
  getUsersByHealthOrganizationId(healthOrganizationId: number) {
    return this.baseService.get<ResultBase<number[]>>(
      `HealthOrganization/${healthOrganizationId}/GetUsersByHealthOrganizationId`
    );
  }
  mapUserToHealthOrganization(
    hoID: number,
    healthOrganizationUser: HealthOrganizationUser
  ): Observable<ResultBase<boolean>> {
    return this.baseService.put<ResultBase<boolean>>(
      `HealthOrganization/${hoID}/MapUserToHealthOrganization`,
      healthOrganizationUser
    );
  }

  deleteEventByID(eventId: number): Observable<any> {
    return this.baseService.delete<any>(
      `Practitioner/DeletePractitionerEvent?practitionerEventId=${eventId}`
    );
  }
}
